import React from "react";
import {Formik} from "formik";
import {FormikSubmitButton} from "../../form/FormikSubmitButton";
import {useAuthsState} from "../../auths/authsContext";
import {FormikTextField} from "../../form/FormikTextField";
import {useSearchTranslation} from "../../search/SearchContext";
import {useEmail} from "../../folder/useEmail";
import {InputRecipientsSearch} from "./InputRecipientsSearch";
import Box from "@mui/material/Box";

/**
 * Panel for sharing a document or a folder with internal DAMS users via email.
 * Extracted from DialogShare – no Dialog wrapper.
 *
 * @param {number}   collectionId      - Collection ID used for recipient lookup.
 * @param {string}   modelId           - UniqueId of the document being shared.
 * @param {string}   folderId          - UniqueId of the folder being shared (if sharing a folder).
 * @param {Function} onShareSuccess    - Called after the email is sent so the
 *                                       dialog can optionally close or proceed.
 */
export const SharePrivatePanel = ({collectionId, modelId, folderId, onShareSuccess}) => {
    const t = useSearchTranslation();
    const {userData} = useAuthsState();
    const {sendEmail} = useEmail();

    const getShareUrl = () => {
        const l = window.location;
        if (folderId) {
            return `${l.protocol}//${l.host}/folders/${folderId}`;
        } else if (modelId) {
            // DE-1740: the document route (DE-1719). Links already sent, /search/?view=<id>, still open the document.
            return `${l.protocol}//${l.host}/documents/${modelId}`;
        }
    }

    const getMessageBody = (message) => {
        const intro = modelId
            ? t("txtShareMessageBody", "har delt et DAMS-dokument med deg")
            : t("txtShareMessageBodyFolder", "har delt en DAMS-folder med deg");

        return "Hei\n\n" +
            userData.name + " " +
            intro +
            ":\n" + getShareUrl() +
            "\n\n" +
            t("txtShareMessageFromSender", "Melding fra avsender") +
            ":\n" + message;
    };

    const getSubject = () => {
        return modelId
            ? t("shareMailSubject", "DAMS delt dokument")
            : t("shareMailSubjectFolder", "DAMS delt folder");
    };

    const handleOnSubmit = (formValues, {resetForm}) => {
        const recipients = formValues.recipients.map(v => v.email);

        sendEmail(
            getSubject(),
            getMessageBody(formValues.message),
            recipients
        );
        resetForm();
        if (onShareSuccess) onShareSuccess();
    };

    return (
        <Formik
            initialValues={{recipients: [], message: ""}}
            validationSchema={null}
            onSubmit={handleOnSubmit}
        >
            {({values}) => (
                <Box sx={{pt: 1, marginTop: '8px'}}>
                    <Box sx={{mb: 2}}>
                        <FormikTextField
                            formikKey="message"
                            label={t("shareDocumentLabelMessage", "Melding")}
                            required={false}
                            fullWidth
                            multiline
                            rows={6}
                            maxLength={256}
                            helperText={t(
                                'dialogShareFilePubliclyAddText',
                                'Legg til en melding som skal sendes til mottagerne av delingen'
                            )}
                            autoFocus
                        />
                    </Box>
                    <InputRecipientsSearch
                        formikKey="recipients"
                        label={t("shareDocumentLabelRecipients", "Mottagere")}
                        disabled={false}
                        fullWidth
                        collectionId={collectionId}
                    />
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                        <FormikSubmitButton color="primary"
                                            variant="contained"
                                            disabled={values.recipients.length === 0}>
                            {t("btnShare", "Del")}
                        </FormikSubmitButton>
                    </Box>
                </Box>
            )}
        </Formik>
    );
};

